const TAG_NAME = 'upd-focus-trap';

const template = document.createElement('template');
template.innerHTML = `
  <style>
    :host {
      display: block;
      outline: none;
    }
  </style>

  <slot></slot>
`;

const ESC_KEY = 27;
const TAB_KEY = 9;

const FOCUSABLE_ELEMENTS_QUERY = [
  'a[href]',
  'area[href]',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  'button:not(:disabled)',
  'iframe',
  'object',
  'embed',
  '[contenteditable]',
].join(',');

// Check for all native `matches` implementations
const proto = window.Element.prototype;
const nativeMatches = proto.matches || proto.msMatchesSelector;
const matches = (query) => (el) => nativeMatches.call(el, query);
const matchesFocus = matches(':focus');
const matchesFocusable = matches(FOCUSABLE_ELEMENTS_QUERY);

const HAS_SHADY_CSS = window.hasOwnProperty('ShadyCSS');
if (HAS_SHADY_CSS) {
  window.ShadyCSS.prepareTemplate(template, TAG_NAME);
}

class FocusTrap extends HTMLElement {
  static get observedAttributes() {
    // Using `is-disabled` since IE11 will restyle inputs/buttons
    // when an ancestor is `disabled`. If we ever drop IE11 support,
    // we might want to revisit the naming here to align with native
    // `disabled` attribute/properties
    return ['is-disabled'];
  }

  constructor() {
    super();

    this.attachShadow({ mode: 'open' });
    this.shadowRoot.appendChild(template.content.cloneNode(true));
  }

  connectedCallback() {
    // Shim Shadow DOM styles. Needs to be run in connectedCallback.
    // Needed for IE 11.
    if (HAS_SHADY_CSS) {
      window.ShadyCSS.styleElement(this);
    }

    this._consumerTabIndex = this.getAttribute('tabindex');
    this._setTabindex();
    this._addListeners();
  }

  disconnectedCallback() {
    this._removeListeners();
    this._restoreFocus();
  }

  attributeChangedCallback(name) {
    if (name === 'is-disabled') {
      if (this.isDisabled) {
        this._removeListeners();
        this._removeTabindex();
        this._restoreFocus();
      } else {
        this._addListeners();
        this._setTabindex();
      }
    }
  }

  set isDisabled(value) {
    value
      ? this.setAttribute('is-disabled', '')
      : this.removeAttribute('is-disabled');
  }

  get isDisabled() {
    return this.hasAttribute('is-disabled');
  }

  _handleKeydown = (event) => {
    if (this.isDisabled) {
      return;
    }

    switch (event.keyCode) {
      case ESC_KEY:
        return this._restoreFocus();
      case TAB_KEY:
        event.preventDefault();
        return this._moveFocus(event.shiftKey ? -1 : 1);
    }
  };

  // Override the native focus as the API to enter into this trap.
  focus() {
    this._previousFocusedElement = deepActiveElement();
    this._moveFocus();
  }

  _restoreFocus() {
    const focusedElement = getTabbableElements(this.shadowRoot).find(
      matchesFocus
    );

    // If focus is already elsewhere, don't do anything
    if (isActiveElementInside(this, focusedElement)) {
      focusedElement && focusedElement.blur();
      this._previousFocusedElement && this._previousFocusedElement.focus();
    }

    this._previousFocusedElement = null;
  }

  _moveFocus(direction = 1) {
    const tabbables = getTabbableElements(this.shadowRoot);
    const index = tabbables.indexOf(deepActiveElement());
    const count = tabbables.length;

    const focusElement = tabbables[(index + direction + count) % count];
    focusElement && focusElement.focus();
  }

  _setTabindex() {
    // If [tabindex] is set, don't clobber it. Otherwise,
    // setting tabindex="-1" on a host with shadow root
    // takes it and all children out of the tab order.
    // This currently only works for browser that support
    // Shadow DOM v1.
    this.setAttribute('tabindex', this._consumerTabIndex || '-1');
  }

  _removeTabindex() {
    this.removeAttribute('tabindex');
  }

  _addListeners() {
    this.addEventListener('keydown', this._handleKeydown);
    this.addEventListener('focus', this._handleFocus);
  }

  _removeListeners() {
    this.removeEventListener('keydown', this._handleKeydown);
    this.removeEventListener('focus', this._handleFocus);
  }
}

window.customElements.define(TAG_NAME, FocusTrap);

function isActiveElementInside(element, activeElement) {
  let a = activeElement;
  while (element !== a && a && (a.assignedSlot || a.parentNode || a.host)) {
    a = a.assignedSlot || a.parentNode || a.host;
  }
  return element === a;
}

function getTabbableElements(parentNode) {
  const tabbable = [];
  composedTreeWalk(parentNode, (node) => {
    if (
      node.nodeType === Node.ELEMENT_NODE &&
      matchesFocusable(node) &&
      node.tabIndex >= 0
    ) {
      tabbable.push(node);
    }
  });

  // Put the tabbable nodes in order based on tabindex
  return tabbable.sort((a, b) => b.tabIndex - a.tabIndex);
}

// Recursively walk the composed tree from node.
// Taken and slightly modified from
// https://github.com/WICG/inert/blob/master/src/inert.js#L607
function composedTreeWalk(node, callback) {
  if (node.nodeType === Node.ELEMENT_NODE) {
    if (callback) {
      callback(node);
    }

    // Descend into node:
    // If it has a ShadowRoot, ignore all child elements - these will be picked
    // up by the <slot> or <shadow> elements. Descend straight into the
    // ShadowRoot.
    const {shadowRoot} = node;
    if (shadowRoot) {
      composedTreeWalk(shadowRoot, callback);
      return;
    }

    // If it is a <slot> element, descend into assigned nodes - these
    // are elements from outside the shadow root which are rendered inside the
    // shadow DOM.
    if (node.localName === 'slot') {
      const distributedNodes = node.assignedNodes
        ? node.assignedNodes({ flatten: true })
        : [];
      distributedNodes.forEach((distributedNode) => {
        composedTreeWalk(distributedNode, callback);
      });
      return;
    }
  }

  // If it is neither the parent of a ShadowRoot, a <slot> element, nor a <shadow> element recurse normally.
  let child = node.firstChild;
  while (child != null) {
    composedTreeWalk(child, callback);
    child = child.nextSibling;
  }
}

// Due to how Shadow DOM currently works, we have to walk
// down all shadowRoots to get the actual activeElement
function deepActiveElement() {
  let a = document.activeElement;
  while (a && a.shadowRoot && a.shadowRoot.activeElement) {
    a = a.shadowRoot.activeElement;
  }
  return a;
}
